/**
 * Samyang Pakistan - install prompt.
 *
 * Android/Chrome fires beforeinstallprompt, so the banner shows a real Install
 * button. iOS Safari has no such event, so it shows the Share-sheet wording
 * instead. Anyone already running the installed app sees nothing.
 */

const DISMISS_KEY = 'sy-install-dismissed';
const DISMISS_DAYS = 14;

/** True when the page is already running as an installed app. */
function isInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent;
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS reports as a Mac, so check for touch as well.
  const iPadOS = /Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function isSafari() {
  const ua = window.navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome/.test(ua);
}

function recentlyDismissed() {
  try {
    const at = parseInt(window.localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!at) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function remember() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Private mode. Not worth handling - the banner simply reappears.
  }
}

class SamyangInstall extends HTMLElement {
  #deferredPrompt = null;

  connectedCallback() {
    if (isInstalled() || recentlyDismissed()) return;

    this.hintEl = this.querySelector('[data-sy-install-hint]');
    this.goBtn = this.querySelector('[data-sy-install-go]');
    this.closeBtn = this.querySelector('[data-sy-install-close]');

    this.closeBtn?.addEventListener('click', () => {
      remember();
      this.hidden = true;
    });

    this.goBtn?.addEventListener('click', async () => {
      if (!this.#deferredPrompt) return;
      this.#deferredPrompt.prompt();
      const { outcome } = await this.#deferredPrompt.userChoice;
      this.#deferredPrompt = null;
      if (outcome !== 'dismissed') remember();
      this.hidden = true;
    });

    // Android and desktop Chrome.
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.#deferredPrompt = event;
      if (this.goBtn) this.goBtn.hidden = false;
      this.#reveal();
    });

    window.addEventListener('appinstalled', () => {
      remember();
      this.hidden = true;
    });

    // iOS never fires beforeinstallprompt, so tell people what to tap.
    if (isIos()) {
      if (this.hintEl) {
        this.hintEl.textContent = isSafari()
          ? 'Tap Share, then Add to Home Screen'
          : 'Open in Safari, then Share → Add to Home Screen';
      }
      this.#reveal(2200);
    }
  }

  /** Held back briefly so the banner never competes with first paint. */
  #reveal(delay = 1200) {
    window.setTimeout(() => {
      if (!recentlyDismissed() && !isInstalled()) this.hidden = false;
    }, delay);
  }
}

if (!customElements.get('sy-install')) {
  customElements.define('sy-install', SamyangInstall);
}
